import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import PasswordForm from '@sayyesschool/frontend/components/password-form';
import { useTeacher } from '@sayyesschool/frontend/features/teachers';
import { UserDomainLabel } from '@sayyesschool/frontend/features/user';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Chip, Grid } from '@sayyesschool/frontend/ui-components';

import TeacherDetails from '@/components/teachers/teacher-details';
import TeacherEnrollments from '@/components/teachers/teacher-enrollments';
import TeacherForm from '@/components/teachers/teacher-form';
import TeacherLessons from '@/components/teachers/teacher-lessons';
import TeacherTasks from '@/components/teachers/teacher-tasks';

export default function TeacherPage() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [teacher, actions] = useTeacher(id);

    const [isTeacherFormOpen, toggleTeacherFormOpen] = useBoolean(false);
    const [isPasswordDialogOpen, togglePasswordDialogOpen] = useBoolean(false);
    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    useEffect(() => {
        if (location.state?.edit) {
            toggleTeacherFormOpen(true);
        } else if (location.state?.delete) {
            toggleConfirmationDialogOpen(true);
        }
    }, []);

    const updateTeacher = useCallback(data => {
        return actions.updateTeacher(teacher.id, data)
            .then(() => toggleTeacherFormOpen(false));
    }, [teacher, actions]);

    const updatePassword = useCallback(password => {
        return actions.updateTeacher(teacher.id, { password })
            .finally(() => togglePasswordDialogOpen(false));
    }, [teacher, actions]);

    const deleteTeacher = useCallback(() => {
        return actions.deleteTeacher(teacher.id)
            .then(() => navigate('/teachers'));
    }, [teacher, actions, navigate]);

    if (!teacher) return <LoadingIndicator />;

    return (
        <Page className="TeacherPage">
            <Page.Header
                title={teacher.fullname}
                breadcrumbs={[
                    { key: 'teachers', content: 'Преподаватели', to: '/teachers' }
                ]}
                description={
                    <Chip.Group
                        chips={teacher.domains.map(domain => ({
                            key: domain,
                            content: UserDomainLabel[domain]
                        }))}
                        variant="plain"
                    />
                }
                actions={[
                    {
                        key: 'password',
                        title: 'Изменить пароль',
                        icon: 'password',
                        onClick: togglePasswordDialogOpen
                    },
                    {
                        key: 'edit',
                        title: 'Редактировать',
                        icon: 'edit',
                        onClick: toggleTeacherFormOpen
                    },
                    {
                        key: 'delete',
                        title: 'Удалить',
                        icon: 'delete',
                        onClick: toggleConfirmationDialogOpen
                    }
                ]}
            />

            <Page.Content>
                <Grid gap="medium">
                    <Grid.Item xs={3}>
                        <TeacherDetails
                            teacher={teacher}
                            onEdit={toggleTeacherFormOpen}
                        />
                    </Grid.Item>

                    <Grid.Item xs={3}>
                        <TeacherLessons teacher={teacher} />
                    </Grid.Item>

                    <Grid.Item xs={3}>
                        <TeacherEnrollments teacher={teacher} />
                    </Grid.Item>

                    <Grid.Item xs={12}>
                        <TeacherTasks teacher={teacher} />
                    </Grid.Item>
                </Grid>
            </Page.Content>

            <FormDialog
                title="Данные преподавателя"
                open={isTeacherFormOpen}
                onClose={toggleTeacherFormOpen}
            >
                <TeacherForm
                    id="teacher-form"
                    teacher={teacher}
                    onSubmit={updateTeacher}
                />
            </FormDialog>

            <FormDialog
                title="Изменение пароля"
                open={isPasswordDialogOpen}
                onClose={togglePasswordDialogOpen}
            >
                <PasswordForm
                    id="password-form"
                    onSubmit={updatePassword}
                />
            </FormDialog>

            <ConfirmationDialog
                title="Подтвердите действие"
                message="Вы действительно хотите удалить преподавателя?"
                open={isConfirmationDialogOpen}
                onConfirm={deleteTeacher}
                onClose={toggleConfirmationDialogOpen}
            />
        </Page>
    );
}